/**
 * ChatMessage - Composite chat row: sm avatar (left), name + timestamp head,
 * and body text. Supports a system style (no avatar, italic gray) and an
 * optional glass-card backdrop.
 */

import { Avatar } from './Avatar.js';
import { BaseUIComponent } from './BaseUIComponent.js';

import type { BaseUIComponentOptions, UIComponentMetadata } from './BaseUIComponent.js';

export interface ChatMessageData {
  /** Sender display name. */
  name: string;
  /** Message body text. */
  body: string;
  /** Optional timestamp string (already formatted). */
  timestamp?: string;
  /** Optional CSS color string applied to the name. */
  color?: string;
  /** Initials fallback for the avatar. */
  initials?: string;
  /** Optional avatar image URL (replaces initials). */
  imageUrl?: string;
  /** Render as a system message: no avatar, italic gray text. */
  system?: boolean;
}

export interface ChatMessageOptions extends BaseUIComponentOptions, ChatMessageData {
  /** Show a glass-card backdrop around the message. */
  glass?: boolean;
}

export class ChatMessage extends BaseUIComponent<ChatMessageOptions> {
  public static metadata: UIComponentMetadata = {
    displayName: 'Chat Message',
    category: 'data',
    summary: 'Chat row with avatar, colored name, timestamp, body, and optional system style.',
    useCases: ['chat', 'lobby chat', 'system message', 'team comms', 'feed entry'],
    optionsType: 'ChatMessageOptions',
    assetPaths: {
      template: '@engine/assets/ui/templates/chat-message.html',
      styles: '@engine/assets/ui/styles/chat-message.css',
    },
  };

  private rootElement: HTMLElement | null = null;
  private avatarSlot: HTMLElement | null = null;
  private avatar: Avatar | null = null;
  private nameElement: HTMLElement | null = null;
  private timestampElement: HTMLElement | null = null;
  private textElement: HTMLElement | null = null;

  protected override getAssetPaths() {
    return {
      templatePath: ChatMessage.metadata.assetPaths.template,
      stylesPath: ChatMessage.metadata.assetPaths.styles,
    };
  }

  protected override getDefaultOptions(): Required<ChatMessageOptions> {
    return {
      position: 'center',
      visible: true,
      customClasses: [],
      customStyles: {},
      name: 'Player',
      body: '',
      timestamp: '',
      color: '',
      initials: '??',
      imageUrl: '',
      system: false,
      glass: false,
    };
  }

  protected override getInitialData(): Record<string, any> {
    return {
      name: this.options.name,
      body: this.options.body,
      timestamp: this.options.timestamp,
      initials: this.options.initials,
    };
  }

  protected override cacheElements(): void {
    if (!this.layout) return;
    this.rootElement = this.layout.querySelector('.ui-chat-message') as HTMLElement | null;
    this.avatarSlot = this.layout.querySelector('[data-chat-message-avatar]') as HTMLElement | null;
    this.nameElement = this.layout.querySelector('[data-chat-message-name]') as HTMLElement | null;
    this.timestampElement = this.layout.querySelector('[data-chat-message-timestamp]') as HTMLElement | null;
    this.textElement = this.layout.querySelector('[data-chat-message-text]') as HTMLElement | null;
  }

  protected override async onInitialize(): Promise<void> {
    if (this.avatarSlot) {
      this.avatar = await this.mountChild(
        Avatar,
        {
          size: 'sm',
          initials: this.options.initials,
          imageUrl: this.options.imageUrl,
        },
        this.avatarSlot,
      );
    }
    this.applyColor();
    this.applySystem();
    this.applyGlass();
  }

  /** Replace the message contents. Unspecified fields are cleared. */
  public setMessage(message: ChatMessageData): void {
    this.options.name = message.name;
    this.options.body = message.body;
    this.options.timestamp = message.timestamp ?? '';
    this.options.color = message.color ?? '';
    this.options.initials = message.initials ?? '??';
    this.options.imageUrl = message.imageUrl ?? '';
    this.options.system = message.system ?? false;

    if (this.nameElement) this.nameElement.textContent = this.options.name;
    if (this.textElement) this.textElement.textContent = this.options.body;
    if (this.timestampElement) this.timestampElement.textContent = this.options.timestamp;
    this.avatar?.setInitials(this.options.initials);
    this.avatar?.setImageUrl(this.options.imageUrl);

    this.applyColor();
    this.applySystem();
  }

  /** Toggle the glass-card backdrop. */
  public setGlass(glass: boolean): void {
    this.options.glass = glass;
    this.applyGlass();
  }

  private applyColor(): void {
    if (!this.nameElement) return;
    if (this.options.color) {
      this.nameElement.style.setProperty('--ui-chat-message-name-color', this.options.color);
    } else {
      this.nameElement.style.removeProperty('--ui-chat-message-name-color');
    }
  }

  private applySystem(): void {
    if (!this.rootElement) return;
    this.rootElement.dataset.system = this.options.system ? 'true' : 'false';
  }

  private applyGlass(): void {
    if (!this.rootElement) return;
    this.rootElement.dataset.glass = this.options.glass ? 'true' : 'false';
  }

  protected override onDestroy(): void {
    // avatar is destroyed by BaseUIComponent.destroy() via mountChild tracking.
    this.avatar = null;
    this.rootElement = null;
    this.avatarSlot = null;
    this.nameElement = null;
    this.timestampElement = null;
    this.textElement = null;
  }
}
